import { createContext, useContext } from 'react'
import { normalizeShopifyShop } from './authenticatedShopBootstrap'

export interface ShopifyAppContextValue {
  shopDomain: string
  orgId: string | null
  connectionId: string | null
  activeScopeKey?: string
  isActiveScope?: (scopeKey: string) => boolean
}

export const ShopifyAppContext = createContext<ShopifyAppContextValue | null>(null)

/** Builds a stable identity for one verified Shopify and TestFeed connection scope. */
export function buildShopifyAppScopeKey(scope: Pick<ShopifyAppContextValue, 'shopDomain' | 'orgId' | 'connectionId'>) {
  return JSON.stringify([scope.shopDomain || 'no-shop', scope.orgId || 'no-org', scope.connectionId || 'no-connection'])
}

/** Safely consumes the ShopifyAppContext. */
export function useShopifyAppContext() {
  const ctx = useContext(ShopifyAppContext)
  if (!ctx) {
    return {
      shopDomain: 'no-shop',
      orgId: null,
      connectionId: null,
    }
  }
  return ctx
}

export interface VerifiedShopifyScope {
  shopDomain: string
}

export interface VerifiedTestFeedConnectionScope {
  shopDomain: string
  orgId: string
  connectionId: string
}

/**
 * Parses and verifies shop domain from an authenticated source.
 * Why: Trim values, reject empty/placeholder values, fail closed.
 */
export function getVerifiedShopifyScope(shopDomain: string | null | undefined): VerifiedShopifyScope | null {
  const normalized = normalizeShopifyShop(shopDomain)
  return normalized ? { shopDomain: normalized } : null
}

/**
 * Parses and verifies readiness status to return the complete TestFeed connection scope.
 * Why: Ensures readiness is genuinely READY, trims, and requires non-empty shop, org, and connection IDs.
 */
export function getVerifiedTestFeedConnectionScope(
  readiness:
    | {
        ready: boolean
        code: string
        externalAccountId?: string | null
        orgId?: string | null
        connectionId?: string | null
      }
    | null
    | undefined,
  authenticatedShopDomain: string | null | undefined,
): VerifiedTestFeedConnectionScope | null {
  if (!readiness) return null

  const verifiedShop = getVerifiedShopifyScope(authenticatedShopDomain)
  if (!verifiedShop) return null

  // Accept connected scope ONLY when readiness is genuinely READY
  if (!readiness.ready || readiness.code !== 'READY') return null

  const shop = readiness.externalAccountId?.trim()
  const org = readiness.orgId?.trim()
  const connection = readiness.connectionId?.trim()

  // Require all fields to be present, non-empty, and match the authenticated shop
  if (!shop || !org || !connection) return null

  // Let's also check if shop matches verifiedShop.shopDomain (though they should, to be safe)
  if (shop !== verifiedShop.shopDomain) {
    return null
  }

  return {
    shopDomain: shop,
    orgId: org,
    connectionId: connection,
  }
}

/** Generates a shop-scoped cache query key. */
export function shopifyQueryKey<const TSegments extends readonly unknown[]>(
  shopDomain: string,
  orgId: string | null | undefined,
  connectionId: string | null | undefined,
  segments: TSegments,
) {
  const finalShop = shopDomain || 'no-shop'
  const finalOrg = orgId || 'no-org'
  const finalConnection = connectionId || 'no-connection'
  return ['shop', finalShop, 'org', finalOrg, 'connection', finalConnection, ...segments] as const
}

/** Generates a setup-status query key scoped by verified shop identity. */
export function shopifySetupStatusKey(shopDomain: string, packetId: string) {
  const finalShop = shopDomain || 'no-shop'
  return ['shop', finalShop, 'setup-status', packetId] as const
}
